import { createSlice } from '@reduxjs/toolkit';
import {
  createInterview,
  fetchInterviews,
  fetchInterviewById,
  fetchInterviewBySession,
  updateInterview,
  submitInterviewResults,
  updateInterviewStatus,
  deleteInterview,
  fetchInterviewStatistics
} from './interviewThunk';

const initialState = {
  // Interview lists
  interviews: [],
  currentInterview: null,
  statistics: null,
  
  // Loading states
  loading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  
  // Error states
  error: null,
  createError: null,
  updateError: null,
  deleteError: null,
  
  // Success messages
  successMessage: null,
  
  // Pagination
  total: 0,
  
  // Configuration for new interview
  interviewConfig: {
    selected_format: 'video',
    duration: 30,
    selected_industry: null,
    selected_role: null,
    selected_company_type: null,
    selected_companies: [],
    ai_persona_id: null,
    ai_persona_name: null,
    ai_accent: 'american',
    ai_tone: 3,
    ai_difficulty: 3,
    ai_pace: 3,
    ai_pressure: 3,
    interview_type: 'behavioral',
    selected_categories: [],
    custom_questions: [],
    enable_follow_ups: true,
    surprise_questions: false,
    star_method_enforcement: false,
    follow_up_intensity: 3,
    adaptive_difficulty: false
  }
};

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    // Configuration actions
    updateInterviewConfig: (state, action) => {
      state.interviewConfig = {
        ...state.interviewConfig,
        ...action.payload
      };
    },
    
    resetInterviewConfig: (state) => {
      state.interviewConfig = initialState.interviewConfig;
    },
    
    setCurrentInterview: (state, action) => {
      state.currentInterview = action.payload;
    },
    
    clearCurrentInterview: (state) => {
      state.currentInterview = null;
    },
    
    clearError: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    
    // Filter interviews by status
    filterInterviewsByStatus: (state, action) => {
      const status = action.payload;
      if (status === 'all') {
        return;
      }
      state.interviews = state.interviews.filter(i => i.status === status);
    }
  },
  
  extraReducers: (builder) => {
    // CREATE INTERVIEW
    builder
      .addCase(createInterview.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.successMessage = null;
      })
      .addCase(createInterview.fulfilled, (state, action) => {
        state.createLoading = false;
        state.successMessage = action.payload.message;
        state.currentInterview = action.payload;
        // Add to interviews list
        state.interviews.unshift({
          id: action.payload.interview_id,
          session_id: action.payload.session_id,
          ...action.payload.configuration,
          status: 'configured'
        });
        state.total += 1;
      })
      .addCase(createInterview.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload || 'Failed to create interview';
      })
    
    // FETCH ALL INTERVIEWS
      .addCase(fetchInterviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInterviews.fulfilled, (state, action) => {
        state.loading = false;
        state.interviews = action.payload?.interviews || [];
        state.total = action.payload?.total || 0;
      })
      .addCase(fetchInterviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch interviews';
        state.interviews = [];
        state.total = 0;
      })
    
    // FETCH INTERVIEW BY ID
      .addCase(fetchInterviewById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInterviewById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInterview = action.payload?.interview || null;
      })
      .addCase(fetchInterviewById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch interview';
        state.currentInterview = null;
      })
    
    // FETCH INTERVIEW BY SESSION
      .addCase(fetchInterviewBySession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInterviewBySession.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInterview = action.payload?.interview || null;
      })
      .addCase(fetchInterviewBySession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch interview';
        state.currentInterview = null;
      })
    
    // UPDATE INTERVIEW
      .addCase(updateInterview.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.successMessage = null;
      })
      .addCase(updateInterview.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.successMessage = action.payload?.message || 'Interview updated';
        state.currentInterview = action.payload?.interview || null;
        
        // Update in list
        if (action.payload?.interview) {
          const index = state.interviews.findIndex(
            i => i.id === action.payload.interview.id
          );
          if (index !== -1) {
            state.interviews[index] = {
              ...state.interviews[index],
              ...action.payload.interview
            };
          }
        }
      })
      .addCase(updateInterview.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || 'Failed to update interview';
      })
    
    // SUBMIT INTERVIEW RESULTS
      .addCase(submitInterviewResults.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.successMessage = null;
      })
      .addCase(submitInterviewResults.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.successMessage = action.payload?.message || 'Results submitted';
        
        if (state.currentInterview && action.payload?.results) {
          state.currentInterview.results = action.payload.results;
          state.currentInterview.status = 'completed';
        }
        
        // Update in list
        const index = state.interviews.findIndex(
          i => i.id === action.meta?.arg?.interviewId
        );
        if (index !== -1 && action.payload?.results) {
          state.interviews[index].status = 'completed';
          state.interviews[index].overall_score = action.payload.results?.scores?.overall_score || 0;
        }
      })
      .addCase(submitInterviewResults.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || 'Failed to submit results';
      })
    
    // UPDATE INTERVIEW STATUS
      .addCase(updateInterviewStatus.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateInterviewStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        
        if (state.currentInterview && action.payload?.interview_id && 
            state.currentInterview.id === action.payload.interview_id) {
          state.currentInterview.status = action.payload.status;
        }
        
        // Update in list
        if (action.payload?.interview_id) {
          const index = state.interviews.findIndex(
            i => i.id === action.payload.interview_id
          );
          if (index !== -1) {
            state.interviews[index].status = action.payload.status;
          }
        }
      })
      .addCase(updateInterviewStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || 'Failed to update status';
      })
    
    // DELETE INTERVIEW
      .addCase(deleteInterview.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.successMessage = null;
      })
      .addCase(deleteInterview.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.successMessage = action.payload?.message || 'Interview deleted';
        
        // Remove from list
        if (action.payload?.interview_id) {
          state.interviews = state.interviews.filter(
            i => i.id !== action.payload.interview_id
          );
          state.total -= 1;
          
          // Clear current if deleted
          if (state.currentInterview && state.currentInterview.id === action.payload.interview_id) {
            state.currentInterview = null;
          }
        }
      })
      .addCase(deleteInterview.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || 'Failed to delete interview';
      })
    
    // FETCH STATISTICS
      .addCase(fetchInterviewStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInterviewStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload?.statistics || null;
      })
      .addCase(fetchInterviewStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch statistics';
        state.statistics = null;
      });
  }
});

// Actions
export const {
  updateInterviewConfig,
  resetInterviewConfig,
  setCurrentInterview,
  clearCurrentInterview,
  clearError,
  clearSuccessMessage,
  filterInterviewsByStatus
} = interviewSlice.actions;

// Selectors
export const selectInterviews = (state) => state.interview.interviews;
export const selectCurrentInterview = (state) => state.interview.currentInterview;
export const selectInterviewConfig = (state) => state.interview.interviewConfig;
export const selectInterviewStatistics = (state) => state.interview.statistics;
export const selectInterviewLoading = (state) => state.interview.loading;
export const selectCreateLoading = (state) => state.interview.createLoading;
export const selectUpdateLoading = (state) => state.interview.updateLoading;
export const selectDeleteLoading = (state) => state.interview.deleteLoading;
export const selectInterviewError = (state) => state.interview.error;
export const selectCreateError = (state) => state.interview.createError;
export const selectUpdateError = (state) => state.interview.updateError;
export const selectDeleteError = (state) => state.interview.deleteError;
export const selectSuccessMessage = (state) => state.interview.successMessage;
export const selectTotal = (state) => state.interview.total;

export default interviewSlice.reducer;